import { Link } from "react-router-dom";

type BrandVariant = {
  number: string;
  name: string;
  description: string;
  surface: string;
  border: string;
  copy: string;
  muted: string;
  wordmark: "dark" | "default";
};

const variants: readonly BrandVariant[] = [
  {
    number: "01",
    name: "Midnight Command",
    description: "The premium dark direction already used on the landing page.",
    surface: "#10151f",
    border: "#2a3545",
    copy: "#f6f8f0",
    muted: "#b3bec8",
    wordmark: "dark",
  },
  {
    number: "02",
    name: "School Teal",
    description: "A confident brand color for a calm, dependable school system.",
    surface: "#156064",
    border: "#2f7d80",
    copy: "#f6f8f0",
    muted: "#b7ded9",
    wordmark: "dark",
  },
  {
    number: "03",
    name: "Ledger Gold",
    description: "A warmer option that connects the mark to fees and accountability.",
    surface: "#f6c32f",
    border: "#e6b41f",
    copy: "#10151f",
    muted: "#5d4700",
    wordmark: "default",
  },
  {
    number: "04",
    name: "Paper & Lime",
    description: "The lightest option for a welcoming, high-clarity public brand.",
    surface: "#eef1e9",
    border: "#c8d2c2",
    copy: "#12201f",
    muted: "#4a4a4a",
    wordmark: "default",
  },
];

export function BrandVariantsPage() {
  return (
    <div className="min-h-screen bg-[#0b1018] text-[#f6f8f0]">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-4 px-5 py-4 lg:px-8">
          <Link to="/" className="font-display text-xl font-extrabold tracking-[-0.03em]">
            Waka<span className="text-[#c9f76f]">skul</span>
          </Link>
          <Link to="/" className="rounded border border-white/20 px-3 py-2 text-sm text-white/75 hover:bg-white/10 hover:text-white">
            Back to site
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1440px] px-5 py-10 lg:px-8 lg:py-14">
        <div className="max-w-3xl">
          <p className="font-mono text-[0.65rem] font-bold uppercase tracking-[0.2em] text-[#c9f76f]">WakaSkul / brand study</p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-[-0.05em] sm:text-6xl">Logo and favicon, four directions.</h1>
          <p className="mt-4 text-base leading-relaxed text-[#b3bec8] sm:text-lg">
            Each option uses the supplied official assets, tuned to a different WakaSkul surface color. Choose one direction before I make it the active brand.
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {variants.map((variant) => (
            <article
              key={variant.name}
              className="overflow-hidden rounded border shadow-[0_20px_50px_rgba(0,0,0,0.2)]"
              style={{ backgroundColor: variant.surface, borderColor: variant.border, color: variant.copy }}
            >
              <div className="flex items-start justify-between gap-4 border-b px-5 py-4" style={{ borderColor: variant.border }}>
                <div>
                  <p className="font-mono text-xs font-bold opacity-55">{variant.number}</p>
                  <h2 className="mt-2 font-display text-xl font-bold">{variant.name}</h2>
                  <p className="mt-1 max-w-md text-sm leading-relaxed" style={{ color: variant.muted }}>{variant.description}</p>
                </div>
                <div
                  className="rounded border p-2"
                  style={{
                    borderColor: variant.border,
                    backgroundColor: variant.wordmark === "dark" ? "#f6f8f0" : "rgba(255,255,255,0.32)",
                  }}
                >
                  <img src="/brand/wakaskul-icon.png" alt="Favicon preview" className="h-14 w-14 object-contain" />
                </div>
              </div>
              <div className="flex min-h-48 items-center justify-center px-6 py-10">
                <img
                  src={variant.wordmark === "dark" ? "/brand/wakaskul-wordmark-dark.png" : "/brand/wakaskul-wordmark.png"}
                  alt={`${variant.name} WakaSkul wordmark`}
                  className="h-auto max-h-24 w-full max-w-[27rem] object-contain"
                />
              </div>
              <div className="flex items-center justify-between border-t px-5 py-3 text-xs" style={{ borderColor: variant.border, color: variant.muted }}>
                <span>Wordmark + favicon</span>
                <span>Responsive-ready</span>
              </div>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}
